import { ReactNode } from 'react';
import { GetLimit, GetStockValue } from '../../../../../../hooks/useProductLock.types';
import { OrderProductError } from '../../../../../../services/modules/order-product/order-product.types';
import { SelloutSummary } from '../../../../../../services/modules/product-color-sellout/product-color-sellout.types';
import { OfflineStatus } from '../../../../../offline/domain/offlineOrder/offlineOrder.types';
import {
  OrderProductSelectedPack,
  PackWithError,
} from '../../../../../products/details/productDetails.types';
import { OrderStatus } from '../../../../interfaces/order-status.interface';
import { CopiedGrid } from '../../../../orderState.types';
import { OrderView } from '../../../../ordersView.types';
import { OnGridValueChange } from '../../../interfaces/on-grid-value-change.interface';
import { OrderProductGridItem, UpdateItemFormat } from '../../../orderProducts.types';
import { SuggestedGridItem } from '../../suggestedGrid/suggested-grid.interface';
import { OrderProductForGridContainer } from '../components/orderProductGrid/components/grid/types/orderProductGridContainer.types';
import {
  HandleSelectPacks,
  HandleSelectSuggestion,
  SelectPacksSideEffect,
} from '../components/orderProductGrid/types/commonGrid.types';

export interface OrderProductGridContextProviderProps {
  children: ReactNode;
  grid: OrderProductGridItem[];
  suggestedGrid: SuggestedGridItem[];
  suggestedPacks: OrderProductSelectedPack[];
  selectedPacks: OrderProductSelectedPack[];
  handleSelectSuggestion?: HandleSelectSuggestion;
  hasValidPacks: boolean;
  productSizeNames: string[];
  orderId?: string;
  orderStatus: OrderStatus | OfflineStatus | undefined;
  productAddedByActions?: boolean;
  disableAll?: boolean;
  hideButtons?: boolean;
  getLimit: GetLimit;
  getStockValue: GetStockValue;
  createOrderItems?: UpdateItemFormat[];
  stackOrderItem: (item: UpdateItemFormat) => void;
  resetSelectedPacks: () => void;
  onClickEnter?: () => void;
  removeCopiedGrid: () => void;
  showSuggestedGrid: boolean;
  suggestionAchieved: boolean;
  handleSelectPacks?: HandleSelectPacks;
  onSelectPacksInstantAction?: () => void;
  selectPacksSideEffect?: SelectPacksSideEffect;
  readMode?: boolean;
  packsWithError?: PackWithError[];
  packsWithUnavailableStock?: PackWithError[];
  highlightProduct?: boolean;
  product: OrderProductForGridContainer;
  handleClickPaste: () => Promise<void>;
  copiedGrid: CopiedGrid;
  copyGrid?: (grid: CopiedGrid) => void;
  selloutSummary?: SelloutSummary;
  validationErrors?: OrderProductError[];
}

export interface OrderProductGridContextProps {
  grid: OrderProductGridItem[];
  suggestedGrid: SuggestedGridItem[];
  suggestedPacks: OrderProductSelectedPack[];
  selectedPacks: OrderProductSelectedPack[];
  stackItem: (item: UpdateItemFormat) => void;
  handleSelectSuggestion?: HandleSelectSuggestion;
  hasValidPacks: boolean;
  productSizeNames: string[];
  isOrderProductFromKit: boolean;
  orderId?: string;
  orderStatus: OrderStatus | OfflineStatus | undefined;
  productAddedByActions?: boolean;
  disableAll: boolean;
  hideButtons: boolean;
  getLimit: GetLimit;
  getStockValue: GetStockValue;
  createOrderItems?: UpdateItemFormat[];
  onClickEnter?: () => void;
  showSuggestedGrid: boolean;
  suggestionAchieved: boolean;
  handleSelectPacks?: HandleSelectPacks;
  onSelectPacksInstantAction?: () => void;
  selectPacksSideEffect?: SelectPacksSideEffect;
  readMode: boolean;
  packsWithError: PackWithError[];
  packsWithUnavailableStock: PackWithError[];
  emptyGrid: boolean;
  orderViewMode?: OrderView;
  handleGridValueChange: (params: OnGridValueChange) => void;
  firstAvailableGrid?: OrderProductGridItem;
  lastAvailableGrid?: OrderProductGridItem;
  hasSuggestedGrid: boolean;
  hasSelectedPacks: boolean;
  highlightProduct: boolean;
  handleClickPaste: () => Promise<void>;
  copiedGrid: CopiedGrid;
  product: OrderProductForGridContainer;
  copyGrid?: (grid: CopiedGrid) => void;
  removeCopiedGrid: () => void;
  selloutSummary?: SelloutSummary;
  gridItemSkuIdsErrorAndDisabled: GridItemSkuIdsErrorAndDisabled[];
  validationErrors?: OrderProductError[];
}

export const orderProductGridContextInitialState: OrderProductGridContextProps = {
  grid: [],
  orderId: '',
  readMode: false,
  getLimit: () => undefined,
  emptyGrid: false,
  stackItem: () => undefined,
  disableAll: false,
  orderStatus: undefined,
  hideButtons: false,
  onClickEnter: () => undefined,
  getStockValue: () => undefined,
  orderViewMode: undefined,
  suggestedGrid: [],
  selectedPacks: [],
  hasValidPacks: false,
  suggestedPacks: [],
  packsWithError: [],
  hasSuggestedGrid: false,
  hasSelectedPacks: false,
  createOrderItems: [],
  productSizeNames: [],
  showSuggestedGrid: false,
  handleSelectPacks: () => undefined,
  lastAvailableGrid: undefined,
  firstAvailableGrid: undefined,
  suggestionAchieved: false,
  productAddedByActions: false,
  isOrderProductFromKit: false,
  handleGridValueChange: () => undefined,
  selectPacksSideEffect: () => undefined,
  handleSelectSuggestion: () => undefined,
  packsWithUnavailableStock: [],
  highlightProduct: false,
  product: {} as OrderProductForGridContainer,
  handleClickPaste: () => Promise.resolve(undefined),
  copiedGrid: { grid: [] },
  removeCopiedGrid: () => undefined,
  selloutSummary: undefined,
  gridItemSkuIdsErrorAndDisabled: [],
  validationErrors: [],
};

export interface GridItemSkuIdsErrorAndDisabled {
  skuId: string;
  error: boolean;
  disabled: boolean;
}
